import React, { useEffect, useId, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button, Segmented } from '../kit';
import LocationChecklist from './LocationChecklist';
import DepartmentEditor from './DepartmentEditor';
import { useOrgDepartments } from './hooks';
import {
  JOURNAL_PERMISSIONS,
  REPORT_GROUPS,
  ROLES,
  ROLE_LABELS,
  applyRoleChange,
  departmentSummary,
  effectiveReportGroups,
  groupCoaDepartments,
  groupDepartmentsByName,
  isAllDepartments,
  isLocationRole,
  toggleReportGroup,
} from './userModel';

const initial = (name) => (String(name || '?').trim()[0] || '?').toUpperCase();

/** Read-only card for an org the admin isn't a member of — kept as-is on save. */
export function LockedOrgCard({ name, entry }) {
  return (
    <div className="us-orgcard us-locked">
      <div className="us-orgcard-head">
        <div className="ak-orgmark ak-sm" aria-hidden="true">{initial(name)}</div>
        <div className="us-orgcard-title">
          <h4>{name}</h4>
          <span className="ak-help">You aren't a member of this organization, so this access is kept as-is.</span>
        </div>
        <span className="ak-role">{ROLE_LABELS[entry?.role] || entry?.role || 'No role'}</span>
      </div>
    </div>
  );
}

/** One-line summary shown when a card is collapsed. */
export function accessSummary(entry, groups) {
  const role = ROLE_LABELS[entry.role] || 'No role yet';
  const reports = groups.length
    ? REPORT_GROUPS.filter((g) => groups.includes(g.value)).map((g) => g.label).join(', ')
    : 'No reports';
  const where = isLocationRole(entry.role)
    ? `${entry.locationIds.length} location${entry.locationIds.length === 1 ? '' : 's'}`
    : 'All locations';
  return `${role} · ${reports} · ${where}`;
}

/** Full-width setting row: label · current value · control. */
function SettingRow({ label, value, children, htmlFor }) {
  return (
    <div className="us-row">
      <div className="us-row-text">
        {htmlFor ? <label className="us-row-title" htmlFor={htmlFor}>{label}</label> : <span className="us-row-title">{label}</span>}
        {value ? <span className="ak-help">{value}</span> : null}
      </div>
      <div className="us-row-actions">{children}</div>
    </div>
  );
}

/**
 * One organization's access: role, report groups, locations (location
 * roles), journal records, labor + GL departments. Collapses to a summary.
 *   resolvedGroups — adminMeta.reportGroups[code] (array | null | undefined)
 */
export default function OrgAccessCard({
  code, org, entry, resolvedGroups, locations, onChange, onRemove, roleError, disabled, defaultOpen = true,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [editing, setEditing] = useState(null); // 'labor' | 'gl' | null
  const locRole = isLocationRole(entry.role);
  const depts = useOrgDepartments(open ? org?.id : null, { labor: locRole });
  const journalId = useId();
  const name = org?.name || code;
  const groups = effectiveReportGroups(entry, resolvedGroups);
  const roleHelp = ROLES.find((r) => r.value === entry.role)?.help;

  // A validation error on a collapsed card opens it so the error is visible.
  useEffect(() => { if (roleError) setOpen(true); }, [roleError]);

  const setJournal = (value) => {
    const next = { ...entry };
    if (value) next.journalPermission = value;
    else delete next.journalPermission;
    onChange(next);
  };

  const laborGroups = groupDepartmentsByName(depts.labor || []);
  const glGroups = groupCoaDepartments(depts.gl || []);
  // Partial selections read as a count: the same department name can sit
  // under several account types, so a name list can't show what's excluded.
  const deptValue = (ids, list) => {
    if (isAllDepartments(ids)) return 'All departments';
    if (!list) return 'Loading…';
    const selected = list.filter((d) => ids.some((x) => String(x) === String(d.id))).length;
    return selected <= 3 ? departmentSummary(ids, list) : `${selected} of ${list.length} departments`;
  };
  const toggleEdit = (which) => setEditing(editing === which ? null : which);

  const head = (
    <div className="us-orgcard-head">
      <div className="ak-orgmark ak-sm" aria-hidden="true">{initial(name)}</div>
      <div className="us-orgcard-title">
        <h4>{name}</h4>
        {!open ? <span className="ak-help">{accessSummary(entry, groups)}</span> : null}
      </div>
      <Button variant="ghost" size="small" onClick={onRemove} disabled={disabled} aria-label={`Remove ${name}`}>Remove</Button>
      <Button
        variant="ghost"
        size="small"
        icon={open ? ChevronUp : ChevronDown}
        aria-expanded={open}
        aria-label={open ? `Collapse ${name}` : `Expand ${name}`}
        onClick={() => setOpen(!open)}
      />
    </div>
  );

  if (!open) {
    return <div className="us-orgcard us-collapsed" id={`us-org-${code}`}>{head}</div>;
  }

  return (
    <div className="us-orgcard" id={`us-org-${code}`} role="group" aria-label={`${name} access`}>
      {head}

      <div className="ak-field" data-error-key={`role:${code}`}>
        <span className="ak-label">Role</span>
        <Segmented
          mode="choice"
          fill
          label={`Role in ${name}`}
          value={entry.role}
          disabled={disabled}
          options={ROLES.map((r) => ({ value: r.value, label: r.label }))}
          onChange={(role) => onChange(applyRoleChange(entry, role))}
        />
        {roleError ? <span className="ak-field-error" role="alert">{roleError}</span> : <span className="ak-help">{roleHelp || 'Choose what this user can do in this organization.'}</span>}
      </div>

      <div className="ak-field">
        <span className="ak-label">Report access</span>
        <div className="us-report-grid">
          {REPORT_GROUPS.map((g) => {
            const on = groups.includes(g.value);
            return (
              <label key={g.value} className={`us-report-card${on ? ' is-on' : ''}${disabled ? ' is-disabled' : ''}`}>
                <input
                  type="checkbox"
                  className="ak-check"
                  checked={on}
                  disabled={disabled}
                  onChange={() => onChange(toggleReportGroup(entry, g.value, resolvedGroups))}
                />
                <span className="us-report-text">
                  <span className="us-report-label">{g.label}</span>
                  <span className="us-report-blurb">{g.blurb}</span>
                </span>
              </label>
            );
          })}
        </div>
        {!Array.isArray(entry.nativeReports) && resolvedGroups !== undefined ? (
          <span className="ak-help">From legacy Power BI grants until changed here.</span>
        ) : null}
      </div>

      {locRole ? (
        <div className="ak-field">
          <span className="ak-label">Locations<span className="ak-req" aria-hidden="true">*</span></span>
          <LocationChecklist
            locations={locations}
            value={entry.locationIds}
            disabled={disabled}
            onChange={(locationIds) => onChange({ ...entry, locationIds })}
          />
          {!entry.locationIds.length ? (
            <span className="us-warn">No locations yet — a location role without locations can't see anything in {name}.</span>
          ) : null}
        </div>
      ) : null}

      <div className="us-rows us-rows-flush">
        <SettingRow label="Journal records" htmlFor={journalId}>
          <select id={journalId} className="ak-select us-row-select" value={entry.journalPermission || ''} disabled={disabled} onChange={(e) => setJournal(e.target.value)}>
            {JOURNAL_PERMISSIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </SettingRow>

        {locRole ? (
          <>
            <SettingRow label="Labor departments" value={deptValue(entry.departmentIds, depts.labor)}>
              <Button size="small" disabled={disabled} aria-expanded={editing === 'labor'} onClick={() => toggleEdit('labor')}>
                {editing === 'labor' ? 'Done' : 'Edit'}
              </Button>
            </SettingRow>
            {editing === 'labor' ? (
              <DepartmentEditor
                title="Labor departments"
                groups={laborGroups}
                ids={entry.departmentIds}
                loading={!depts.labor}
                onChange={(ids) => onChange({ ...entry, departmentIds: ids })}
              />
            ) : null}
          </>
        ) : null}

        <SettingRow label="GL departments" value={deptValue(entry.chartOfAccountDepartmentIds, depts.gl)}>
          <Button size="small" disabled={disabled} aria-expanded={editing === 'gl'} onClick={() => toggleEdit('gl')}>
            {editing === 'gl' ? 'Done' : 'Edit'}
          </Button>
        </SettingRow>
        {editing === 'gl' ? (
          <DepartmentEditor
            title="GL departments"
            groups={glGroups}
            ids={entry.chartOfAccountDepartmentIds}
            loading={!depts.gl}
            onChange={(ids) => onChange({ ...entry, chartOfAccountDepartmentIds: ids })}
          />
        ) : null}
      </div>
    </div>
  );
}
